import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../AuthContext";
import { useNavigate } from "react-router-dom";

// Heroicons CheckIcon SVG (animated)
function AnimatedCheckIcon({ show }) {
    return (
        <div
            className={`flex justify-center items-center transition-opacity duration-500 fixed inset-0 z-50 bg-white bg-opacity-80 ${
                show ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
            }`}
        >
            <svg
                className="w-24 h-24 text-emerald-500 animate-bounce"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
            >
                <circle
                    className="text-emerald-100"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="currentColor"
                    fillOpacity="0.1"
                />
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12l2 2 4-4"
                />
            </svg>
        </div>
    );
}

export default function WitnessPasswordInput() {
    const [value, setValue] = useState("");
    const [form, setForm] = useState(false);
    const [showCheck, setShowCheck] = useState(false);
    const [witnessStatement, setWitnessStatement] = useState([]);
    const [anonymous, setAnonymous] = useState(false);
    const [statement, setStatement] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const user = useContext(AuthContext); // Access the user state from AuthContext

    const navigate = useNavigate(); // Initialize useNavigate

    useEffect(() => {
        if (value.length === 4 && /^\d{4}$/.test(value)) {
            const getWitnessStatementRequest = async () => {
                try {
                    const response = await fetch('https://api.insightos.co.uk/api/witnessstatement', {
                        method: 'GET',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                    });

                    if (response.ok) {
                        const data = await response.json();
                        const witnessStatements = data.data || [];
                        const pinMatch = witnessStatements.some(
                            (ws) => ws.Pin && ws.Pin.toString() === value
                        );

                        if (pinMatch) {
                            setShowCheck(true);
                            setWitnessStatement(witnessStatements);
                            setTimeout(() => {
                                setShowCheck(false);
                                setForm(true);
                            }, 2000);
                        }
                    }
                } catch (error) {
                    // Handle error
                }
            };

            getWitnessStatementRequest();
        }
    }, [value]);

    const handlePinChange = (e) => {
        const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
        setValue(val);
    };

    const handleAnonymousChange = (e) => {
        setAnonymous(e.target.checked);
    };

    const handleStatementChange = (e) => {
        setStatement(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const response = await fetch("https://api.insightos.co.uk/api/submitwitness", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    anonymous,
                    statement,
                    Id: witnessStatement.map((statement) => statement.Id),
                }),
                credentials: "include",
            });
            // Optionally show success/failure feedback here
        // wait for success message
        if (response.ok) {
            const data = await response.json();
            if (data.success) {
                setShowCheck(true);
                setTimeout(() => {
                    if (user.access === "staff") {
                        navigate("/staffdash" );
                    } else if (user.access === "manager" || user.access === "admin") {
                        navigate("/dashboard" );
                    }

                }, 2000);
            }


        } else {
            const errorData = await response.json();
            console.error("Error submitting statement:", errorData.message);
        }
        }
        catch (error) {
            console.error("Error submitting statement:", error);
        } finally {
            setSubmitting(false);
        }
    }
    

    return (
        <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gray-50 px-2 sm:px-0">
            <AnimatedCheckIcon show={showCheck} />
            {!form ? (
                <div className="w-full max-w-sm sm:max-w-md md:max-w-lg flex flex-col items-center">
                    <input
                        type="password"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={4}
                        value={value}
                        onChange={handlePinChange}
                        className="w-full h-16 text-3xl text-center border-2 border-gray-300 rounded-lg focus:border-emerald-500 focus:outline-none transition-all bg-white shadow mb-4"
                        placeholder="Enter 4-digit PIN"
                        autoComplete="off"
                    />
                </div>
            ) : (
                <form
                    className="w-full max-w-lg bg-white shadow-lg rounded-lg p-4 sm:p-8 space-y-6 mx-auto"
                    onSubmit={handleSubmit}
                >
                    <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-2">Witness Statement Form</h2>
                    <p className="text-xs sm:text-sm text-gray-600 mb-4">
                    This witness statement is part of a formal internal investigation. All information submitted must be truthful, accurate, and based solely on your own knowledge or observations.

Once submitted, you will not be able to edit, redact, or withdraw your statement. Please ensure all details are correct before completing the form.

Knowingly providing false or misleading information may be considered gross misconduct and could result in disciplinary action, including dismissal.

If you choose to remain anonymous, your identity will be hidden from the individuals involved in the investigation. However, the investigating officer will be aware of your identity and will treat it with the highest level of confidentiality.

Do not discuss this matter with anyone, including colleagues or third parties, to protect the integrity of the investigation and prevent potential interference or influence.

If you are feeling stressed, uncomfortable, or affected by this process, we encourage you to reach out to your line manager, HR, or the appropriate employee support services within the company for guidance and support.
                    </p>
                    <div className="mb-4 mt-4">
                        {witnessStatement.map((statement) => {
                            const disc = statement.disciplinary || {};
                            return (
                                <p className="text-xs sm:text-sm text-gray-600 mb-4" key={statement._id || statement.Pin || Math.random()}>
                                    Regarding the Incident involving <strong> {disc.Author || "Unknown"} </strong> On The <strong> {disc.DateOfIncident ? new Date(disc.DateOfIncident).toLocaleDateString() : "Unknown Date"} </strong> At <strong> {disc.TimeOfIncident || "Unknown Time"}.</strong> 
                                </p>
                            );
                        })}
                    </div>
                    <div className="flex items-center mb-4">
                        <input
                            id="anonymous"
                            type="checkbox"
                            className="h-5 w-5 text-emerald-500 border-gray-300 rounded focus:ring-emerald-500"
                            checked={anonymous === "Yes"}
                            onChange={e => setAnonymous(e.target.checked ? "Yes" : "No")}
                        />
                        <label htmlFor="anonymous" className="ml-2 text-gray-700 text-base">
                            Do you wish to stay Anonymous?
                        </label>
                    </div>
                    <div className="mb-4">
                        <label htmlFor="statement" className="block text-gray-700 text-base font-medium mb-2">
                            Your Statement
                        </label>
                        <textarea
                            id="statement"
                            rows={6}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-emerald-500 focus:outline-none resize-none text-gray-800"
                            placeholder="Write your statement here..."
                            value={statement}
                            onChange={handleStatementChange}
                        />
                    </div>
                    <button
                        type="submit"
                        className="w-full py-3 px-6 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg shadow transition-all"
                        disabled={submitting}
                    >
                        {submitting ? "Submitting..." : "Submit Statement"}
                    </button>
                </form>
            )}
        </div>
    );
}
